import React, { useState, useEffect } from 'react';
import {
  FaCloudUploadAlt,
  FaFileUpload,
  FaInfoCircle,
} from 'react-icons/fa';
import UploadFlow from './UploadFlow';
import ExtractionFlow from './ExtractionFlow';
import { styles } from './styles';
import { 
  handleAddSection,
  handleEditSection,
  handleDeleteSection,
  handleClearSections,
  handleCancelEdit,
  removeFile
} from './utils/uploadContentUtils';

const UploadContent = ({ onExtractionComplete }) => {
  const [mode, setMode] = useState('upload'); // 'upload' or 'extraction'
  
  // Add state to track if we're in extraction flow
  const [isInExtractionFlow, setIsInExtractionFlow] = useState(false);
  const [extractionData, setExtractionData] = useState(null);
  
  // Add state for extraction flow disabled
  const [extractionFlowDisabled, setExtractionFlowDisabled] = useState(true);
  const [showDisabledMessage, setShowDisabledMessage] = useState(false);
  
  // Upload flow states
  const [selectedFileType, setSelectedFileType] = useState('Annual Files');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [tickerId, setTickerId] = useState('');
  const [showFilesModal, setShowFilesModal] = useState(false);
  
  // Extraction flow states
  const [extractionFiles, setExtractionFiles] = useState([]);
  const [sections, setSections] = useState([]);
  const [currentSection, setCurrentSection] = useState({
    selectedFile: '',
    companyName: '',
    tickerId: '',
    pageRange: '',
    title: [],
    periodType: ''
  });
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showExtractionFiles, setShowExtractionFiles] = useState(false);

  // Static options
  const titleOptions = ['Income Statement', 'Balance Sheet', 'Cash Flow'];
  const periodTypeOptions = ['Annual', 'Quarterly'];

  // Mock financial data
  const financialStatementsData = {
    incomeStatement: [],
    balanceSheet: [],
    cashFlow: []
  };

  // UPDATED: Simplified extraction complete handler
  const handleExtractionCompleteWrapper = (extractedData, extractionFiles, sections) => {
    console.log('Upload completed with message:', extractedData?.message);
    
    // Just show message and stay in upload mode
    // No redirection to DataContent
    setIsInExtractionFlow(false);
    setExtractionData(null);
    
    // Note: The success message is already shown in handleUploadModeExtraction
    // We don't need to do anything else here
  };

  // Section input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSection(prev => ({ ...prev, [name]: value }));
  };

  const handleCompanyInputChange = (e) => {
    const { value } = e.target;
    setCurrentSection(prev => ({ ...prev, companyName: value }));
  };

  const handleTickerInputChange = (e) => {
    const { value } = e.target;
    setCurrentSection(prev => ({ ...prev, tickerId: value }));
  };

  const handleTitleSelection = (title) => {
    setCurrentSection(prev => {
      if (prev.title.includes(title)) {
        return {
          ...prev,
          title: prev.title.filter(t => t !== title)
        };
      }
      return {
        ...prev,
        title: [...prev.title, title]
      };
    });
  };

  const handlePeriodTypeSelection = (periodType) => {
    setCurrentSection(prev => ({ ...prev, periodType }));
  };

  // Wrapper functions for section management
  const handleAddSectionWrapper = () => {
    handleAddSection(
      currentSection,
      editingSectionId,
      extractionFiles,
      sections,
      setSections,
      setCurrentSection,
      setEditingSectionId
    );
  };

  const handleEditSectionWrapper = (section) => {
    handleEditSection(section, setCurrentSection, setEditingSectionId);
  };

  const handleDeleteSectionWrapper = (id) => {
    handleDeleteSection(id, sections, setSections);
  };

  const handleClearSectionsWrapper = () => {
    handleClearSections(sections, setSections);
  };

  const handleCancelEditWrapper = () => {
    handleCancelEdit(setCurrentSection, setEditingSectionId);
  };

  const handleRemoveFileWrapper = (id, isExtraction = false) => {
    if (isExtraction) {
      removeFile(id, extractionFiles, setExtractionFiles, sections, setSections, true);
    } else {
      removeFile(id, uploadedFiles, setUploadedFiles, [], () => {}, false);
    }
  };

  const handleUpdateSectionWrapper = () => {
    handleAddSectionWrapper();
  };

  // Handle mode change with disabled extraction flow
  const handleModeChange = (newMode) => {
    // If trying to switch to extraction flow when it's disabled
    if (newMode === 'extraction' && extractionFlowDisabled) {
      setShowDisabledMessage(true);
      return;
    }
    
    // If trying to switch modes while in extraction flow, confirm first
    if (isInExtractionFlow && mode !== newMode) {
      const confirmChange = window.confirm(
        'You have an ongoing extraction. Switching modes will cancel it. Continue?'
      );
      if (!confirmChange) return;
      setIsInExtractionFlow(false);
    }
    setMode(newMode);
    setShowDisabledMessage(false);
  };

  return (
    <div className="p-6 overflow-y-auto max-h-[calc(100vh-150px)]">
      <div className="max-w-7xl mx-auto">
        
        {/* Mode Toggle Switch */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Document Processing
            </h2>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              mode === 'upload' 
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' 
                : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
            }`}>
              {mode === 'upload' ? 'Upload Mode' : 'Extraction Mode'}
            </span>
          </div>
          
          <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-lg inline-flex relative">
            <button
              type="button"
              onClick={() => handleModeChange('upload')}
              className={styles.modeToggleButton(mode === 'upload', 'upload')}
            >
              <FaCloudUploadAlt />
              Upload Files
            </button>
            <button
              type="button"
              onClick={() => handleModeChange('extraction')}
              className={`flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium transition-all ${
                mode === 'extraction'
                  ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-sm'
                  : extractionFlowDisabled
                    ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
              }`}
              disabled={extractionFlowDisabled}
            >
              <FaFileUpload />
              Data Extraction
              {extractionFlowDisabled && (
                <div className="absolute -top-1 -right-1">
                  <div className="relative">
                    <span className="flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-500"></span>
                    </span>
                  </div>
                </div>
              )}
            </button>
          </div>
          
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
            {mode === 'upload' 
              ? 'Upload PDF documents for processing and storage' 
              : 'Define extraction areas and extract data'}
          </p>

          {/* Disabled Extraction Flow Message */}
          {showDisabledMessage && (
            <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
              <div className="flex items-start gap-3">
                <FaInfoCircle className="text-yellow-500 text-lg mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-yellow-800 dark:text-yellow-300">
                    Data Extraction Flow - Coming Soon!
                  </h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                    The manual data extraction flow is currently under development. 
                    Please use the <span className="font-semibold">"Upload Files"</span> mode 
                    for automated data extraction and processing through our backend system.
                  </p>
                  <div className="mt-3 text-sm text-yellow-600 dark:text-yellow-500">
                    <p className="font-medium">Current Features in Upload Mode:</p>
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      <li>Automated PDF processing and data extraction</li>
                      <li>Company name search with suggestions</li>
                      <li>Real-time progress tracking</li>
                      <li>Excel file generation</li>
                      <li>Backend job management</li>
                    </ul>
                  </div>
                  <button
                    onClick={() => {
                      setShowDisabledMessage(false);
                      setMode('upload');
                    }}
                    className="mt-3 px-4 py-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 
                               hover:bg-yellow-200 dark:hover:bg-yellow-900/40 rounded-md text-sm font-medium 
                               transition-colors border border-yellow-300 dark:border-yellow-700"
                  >
                    Switch to Upload Mode
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Render the appropriate flow based on mode */}
        {mode === 'upload' ? (
          <UploadFlow
            selectedFileType={selectedFileType}
            setSelectedFileType={setSelectedFileType}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            companyName={companyName}
            setCompanyName={setCompanyName}
            tickerId={tickerId}
            setTickerId={setTickerId}
            showFilesModal={showFilesModal}
            setShowFilesModal={setShowFilesModal}
            setIsProcessing={setIsProcessing}
            isProcessing={isProcessing}
            financialStatementsData={financialStatementsData}
            onExtractionComplete={handleExtractionCompleteWrapper}
            removeFile={handleRemoveFileWrapper}
          />
        ) : extractionFlowDisabled ? (
          // Show disabled state message in extraction flow area
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full mb-4">
                <FaFileUpload className="text-2xl text-yellow-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Data Extraction Flow - Under Development
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-6">
                The manual data extraction interface is currently being enhanced. 
                Please use the Upload Files mode for automated processing.
              </p>
              <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-4 max-w-md mx-auto mb-6">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Why use Upload Mode?
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                  <li>• Fully automated data extraction</li>
                  <li>• Built-in company verification</li>
                  <li>• Real-time progress tracking</li>
                  <li>• Excel file generation</li>
                  <li>• Database integration</li>
                </ul>
              </div>
              <button
                onClick={() => setMode('upload')}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md 
                           font-medium transition-colors shadow-sm"
              >
                Switch to Upload Files Mode
              </button>
            </div>
          </div>
        ) : (
          <ExtractionFlow
            extractionFiles={extractionFiles}
            setExtractionFiles={setExtractionFiles}
            sections={sections}
            setSections={setSections}
            currentSection={currentSection}
            setCurrentSection={setCurrentSection}
            editingSectionId={editingSectionId}
            setEditingSectionId={setEditingSectionId}
            isProcessing={isProcessing}
            setIsProcessing={setIsProcessing}
            showExtractionFiles={showExtractionFiles}
            setShowExtractionFiles={setShowExtractionFiles}
            titleOptions={titleOptions}
            periodTypeOptions={periodTypeOptions}
            financialStatementsData={financialStatementsData}
            onExtractionComplete={handleExtractionCompleteWrapper}
            removeFile={handleRemoveFileWrapper}
            // Section management handlers
            handleInputChange={handleInputChange}
            handleCompanyInputChange={handleCompanyInputChange}
            handleTickerInputChange={handleTickerInputChange}
            handleTitleSelection={handleTitleSelection}
            handlePeriodTypeSelection={handlePeriodTypeSelection}
            handleCancelEdit={handleCancelEditWrapper}
            handleUpdateSection={handleUpdateSectionWrapper}
            handleAddSection={handleAddSectionWrapper}
            handleEditSection={handleEditSectionWrapper}
            handleDeleteSection={handleDeleteSectionWrapper}
            handleClearSections={handleClearSectionsWrapper}
          />
        )}
      </div>
    </div>
  );
};

export default UploadContent;